import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 40, left: 50 };
const ROW_HEIGHT = 35;

const SORT_OPTIONS = [
  { value: 'spread_desc', label: 'Spread (widest)' },
  { value: 'spread_asc', label: 'Spread (narrowest)' },
  { value: 'state_asc', label: 'State' },
  { value: 'avg_desc', label: 'Avg Price' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  sortWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6b7280',
    flexShrink: 0,
  },
  sortSelect: {
    fontSize: '12px',
    padding: '4px 8px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function DumbbellChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [sortBy, setSortBy] = useState('spread_desc');

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data];
    switch (sortBy) {
      case 'spread_desc':
        sorted.sort((a, b) => b.spread - a.spread);
        break;
      case 'spread_asc':
        sorted.sort((a, b) => a.spread - b.spread);
        break;
      case 'state_asc':
        sorted.sort((a, b) => a.state.localeCompare(b.state));
        break;
      case 'avg_desc':
        sorted.sort((a, b) => b.avgPrice - a.avgPrice);
        break;
      default:
        break;
    }
    return sorted;
  }, [data, sortBy]);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(500, containerWidth);
    const height = sortedData.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const xMin = d3.min(sortedData, (d) => d.minPrice);
    const xMax = d3.max(sortedData, (d) => d.maxPrice);
    const xPadding = (xMax - xMin) * 0.1 || 5;

    const x = d3.scaleLinear()
      .domain([Math.max(0, xMin - xPadding), xMax + xPadding])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(sortedData.map((d) => d.state))
      .range([0, innerH])
      .padding(0.3);

    // Horizontal grid lines
    g.selectAll('.grid-h')
      .data(x.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis (bottom)
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis (left - state names)
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', '500');

    // Draw dumbbells
    sortedData.forEach((d) => {
      const cy = y(d.state) + y.bandwidth() / 2;

      // Row group for hover handling
      const rowGroup = g.append('g')
        .attr('class', 'dumbbell-row')
        .style('cursor', 'pointer');

      // Invisible hit area for the entire row
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', y(d.state))
        .attr('width', innerW)
        .attr('height', y.bandwidth())
        .attr('fill', 'transparent');

      // Connecting bar
      rowGroup.append('line')
        .attr('x1', x(d.minPrice))
        .attr('x2', x(d.maxPrice))
        .attr('y1', cy)
        .attr('y2', cy)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      // Min dot (blue)
      rowGroup.append('circle')
        .attr('cx', x(d.minPrice))
        .attr('cy', cy)
        .attr('r', 7)
        .attr('fill', '#2563eb');

      // Max dot (red)
      rowGroup.append('circle')
        .attr('cx', x(d.maxPrice))
        .attr('cy', cy)
        .attr('r', 7)
        .attr('fill', '#dc2626');

      // Avg dot (amber diamond shape using a rotated square path)
      const avgX = x(d.avgPrice);
      const diamondSize = 5;
      rowGroup.append('path')
        .attr('d', `M${avgX},${cy - diamondSize} L${avgX + diamondSize},${cy} L${avgX},${cy + diamondSize} L${avgX - diamondSize},${cy} Z`)
        .attr('fill', '#f59e0b');

      // Spread label
      const spreadLabelX = x(d.maxPrice) + 8;
      rowGroup.append('text')
        .attr('x', spreadLabelX)
        .attr('y', cy)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(`$${d.spread.toFixed(2)} spread`);

      // Hover interactions
      rowGroup
        .on('mouseenter', (event) => {
          // Brighten the row
          rowGroup.select('line').attr('stroke', '#9ca3af').attr('stroke-width', 4);
          rowGroup.selectAll('circle').attr('r', 9);
          rowGroup.select('path').attr('transform', `scale(1.3) translate(${avgX * (1 - 1 / 1.3)},${cy * (1 - 1 / 1.3)})`);
          // Reset path transform approach - use a simpler brightening
          rowGroup.select('path')
            .attr('d', () => {
              const s = diamondSize * 1.3;
              return `M${avgX},${cy - s} L${avgX + s},${cy} L${avgX},${cy + s} L${avgX - s},${cy} Z`;
            });

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            minPrice: d.minPrice,
            avgPrice: d.avgPrice,
            maxPrice: d.maxPrice,
            spread: d.spread,
            addressCount: d.addressCount,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev
              ? {
                  ...prev,
                  x: event.clientX + 12,
                  y: event.clientY - 10,
                }
              : prev
          );
        })
        .on('mouseleave', () => {
          rowGroup.select('line').attr('stroke', '#d1d5db').attr('stroke-width', 3);
          rowGroup.selectAll('circle').attr('r', 7);
          rowGroup.select('path')
            .attr('d', () => {
              return `M${avgX},${cy - diamondSize} L${avgX + diamondSize},${cy} L${avgX},${cy + diamondSize} L${avgX - diamondSize},${cy} Z`;
            });
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedData]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Range by State</div>
        <div style={styles.subtitle}>Min to max pest365 price spread per state</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No price range data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Price Range by State</div>
          <div style={styles.subtitle}>Min to max pest365 price spread per state</div>
        </div>
        <div style={styles.sortWrapper}>
          <span>Sort by:</span>
          <select
            style={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#2563eb' }} />
          <span>Min Price</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#f59e0b' }} />
          <span>Avg Price</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#dc2626' }} />
          <span>Max Price</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>Min</span>
            <span style={{ fontWeight: 600 }}>${tooltip.minPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#f59e0b' }}>Avg</span>
            <span style={{ fontWeight: 600 }}>${tooltip.avgPrice.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#dc2626' }}>Max</span>
            <span style={{ fontWeight: 600 }}>${tooltip.maxPrice.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Spread</span>
              <span style={{ fontWeight: 600 }}>${tooltip.spread.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Addresses</span>
              <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
